import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

const base = process.env.REACT_APP_BACKEND_BASE_URL;

const academicYears = () => {
  const years = [];
  for (let start = 2023; start <= 2035; start += 1) {
    years.push(`${start}-${start + 1}`);
  }
  return years;
};

const currentAcademicYear = () => {
  const now = new Date();
  const start = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}-${start + 1}`;
};

const previousAcademicYear = (year) => {
  const start = Number(String(year).split("-")[0]);
  if (!start) return "";
  return `${start - 1}-${start}`;
};

const emptyClass = {
  id_branch: "",
  id_board: "",
  id_medium: "",
  id_standard: "",
  id_batch: "",
  id_course: "",
  id_shift: "",
};

function CarryForward() {
  const userData = JSON.parse(localStorage.getItem("userData") || "null");
  const allowed = userData?.roll === "Founder" || userData?.roll === "Admin";
  const years = useMemo(() => academicYears(), []);
  const defaultToYear = currentAcademicYear();

  const [tab, setTab] = useState("promote");
  const [branches, setBranches] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [fromLists, setFromLists] = useState({ boards: [], mediums: [], standards: [], batches: [] });
  const [toLists, setToLists] = useState({ boards: [], mediums: [], standards: [], batches: [], courses: [] });
  const [fromClass, setFromClass] = useState({ ...emptyClass });
  const [toClass, setToClass] = useState({ ...emptyClass });
  const [fromYear, setFromYear] = useState(previousAcademicYear(defaultToYear));
  const [toYear, setToYear] = useState(defaultToYear);
  const [remark, setRemark] = useState("");
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [historyYear, setHistoryYear] = useState("");
  const [yearRecord, setYearRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [branchRes, shiftRes] = await Promise.all([
          axios.get(`${base}/branch`),
          axios.get(`${base}/shift`),
        ]);
        setBranches(branchRes.data?.data || []);
        setShifts(shiftRes.data?.data || []);
      } catch (error) {
        console.error(error);
      }
    };
    if (allowed) loadMasters();
  }, [allowed]);

  const loadChain = async (which, field, value, current) => {
    const next = { ...current, [field]: value };
    const setClass = which === "from" ? setFromClass : setToClass;
    const setLists = which === "from" ? setFromLists : setToLists;

    if (field === "id_branch") {
      next.id_board = "";
      next.id_medium = "";
      next.id_standard = "";
      next.id_batch = "";
      next.id_course = "";
    }
    if (field === "id_board") {
      next.id_medium = "";
      next.id_standard = "";
      next.id_batch = "";
      next.id_course = "";
    }
    if (field === "id_medium") {
      next.id_standard = "";
      next.id_batch = "";
      next.id_course = "";
    }
    if (field === "id_standard") {
      next.id_batch = "";
      next.id_course = "";
    }
    setClass(next);
    if (which === "from") {
      setStudents([]);
      setSelected([]);
    }

    try {
      if (field === "id_branch" && value) {
        const res = await axios.get(`${base}/boardbybr/?id_branch=${value}`);
        setLists((prev) => ({ ...prev, boards: res.data?.data || [], mediums: [], standards: [], batches: [], courses: [] }));
      }
      if (field === "id_board" && value) {
        const res = await axios.get(`${base}/mediumbybrbo/?id_branch=${next.id_branch}&id_board=${value}`);
        setLists((prev) => ({ ...prev, mediums: res.data?.data || [], standards: [], batches: [], courses: [] }));
      }
      if (field === "id_medium" && value) {
        const res = await axios.get(
          `${base}/standardbybrbomd/?branch_id=${next.id_branch}&id_board=${next.id_board}&id_medium=${value}`
        );
        setLists((prev) => ({ ...prev, standards: res.data?.data || [], batches: [], courses: [] }));
      }
      if (field === "id_standard" && value) {
        const batchRes = await axios.get(
          `${base}/batchbybrbomdst/?id_branch=${next.id_branch}&id_board=${next.id_board}&id_medium=${next.id_medium}&id_standard=${value}`
        );
        const updates = { batches: batchRes.data?.data || [] };
        if (which === "to") {
          const courseRes = await axios.get(
            `${base}/getCoursebyids/?id_branch=${next.id_branch}&id_board=${next.id_board}&id_medium=${next.id_medium}&id_standard=${value}`
          );
          updates.courses = courseRes.data?.data || [];
        }
        setLists((prev) => ({ ...prev, ...updates }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const copyCurrentSetup = async () => {
    setToClass((prev) => ({
      ...prev,
      id_branch: fromClass.id_branch,
      id_board: fromClass.id_board,
      id_medium: fromClass.id_medium,
      id_standard: "",
      id_batch: "",
      id_course: "",
    }));
    setToLists({
      boards: fromLists.boards,
      mediums: fromLists.mediums,
      standards: fromLists.standards,
      batches: [],
      courses: [],
    });
  };

  const showStudents = async () => {
    if (!fromClass.id_branch || !fromClass.id_board || !fromClass.id_medium || !fromClass.id_standard) {
      Swal.fire("Select the current class", "Branch, board, medium and standard are required.", "warning");
      return;
    }
    setLoadingStudents(true);
    try {
      const res = await axios.get(`${base}/promotion/students`, { params: fromClass });
      setStudents(res.data?.data || []);
      setSelected([]);
    } catch (error) {
      Swal.fire("Could not load students", error.response?.data?.msg || "Try again.", "error");
    } finally {
      setLoadingStudents(false);
    }
  };

  const visibleStudents = students.filter((student) =>
    student.name?.toLowerCase().includes(search.trim().toLowerCase())
  );

  const toggleAll = (checked) => {
    setSelected(checked ? visibleStudents.map((student) => student.id) : []);
  };

  const toggleOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const carryForward = async () => {
    if (!selected.length) {
      Swal.fire("Select students", "Choose the students who will move to the next standard.", "warning");
      return;
    }
    if (!toClass.id_branch || !toClass.id_board || !toClass.id_medium || !toClass.id_standard || !toClass.id_batch) {
      Swal.fire("Select the next class", "Next branch, board, medium, standard and batch are required.", "warning");
      return;
    }
    if (String(fromClass.id_standard) === String(toClass.id_standard) && String(fromClass.id_branch) === String(toClass.id_branch)) {
      Swal.fire("Choose the next standard", "A student cannot be carried forward into the same standard.", "warning");
      return;
    }
    if (fromYear === toYear) {
      Swal.fire("Choose two years", "The completed year and the next year must be different.", "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: "Carry forward students?",
      text: `${selected.length} student(s) will move to the next standard. Attendance, exams, homework and fees of the old year stay unchanged.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#5000C0",
      confirmButtonText: "Yes, carry forward",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const res = await axios.post(`${base}/promotion/carry-forward`, {
        student_ids: selected,
        from_academic_year: fromYear,
        to_academic_year: toYear,
        from_branch: fromClass.id_branch,
        from_board: fromClass.id_board,
        from_medium: fromClass.id_medium,
        from_standard: fromClass.id_standard,
        to_branch: toClass.id_branch,
        to_board: toClass.id_board,
        to_medium: toClass.id_medium,
        to_standard: toClass.id_standard,
        to_batch: toClass.id_batch,
        to_course: toClass.id_course,
        to_shift: toClass.id_shift,
        promoted_by: userData?.data?.id,
        remark,
      });
      const skipped = res.data?.data?.skipped || [];
      const promoted = res.data?.data?.promoted || [];
      const skipText = skipped.map((item) => `${item.name || item.id}: ${item.reason}`).join("<br/>");
      Swal.fire({
        title: promoted.length ? "Carry forward saved" : "No student was moved",
        html: skipText ? `${res.data.msg}<br/>${skipText}` : res.data.msg,
        icon: promoted.length ? "success" : "warning",
      });
      setStudents([]);
      setSelected([]);
    } catch (error) {
      Swal.fire("Carry forward failed", error.response?.data?.msg || "No student was moved.", "error");
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${base}/promotion/history`, {
        params: { search: historySearch, academic_year: historyYear },
      });
      setHistory(res.data?.data || []);
      setYearRecord(null);
    } catch (error) {
      Swal.fire("Could not load history", error.response?.data?.msg || "Try again.", "error");
    }
  };

  useEffect(() => {
    if (allowed && tab === "history") loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const openYear = async (row) => {
    setLoadingRecord(true);
    try {
      const res = await axios.get(`${base}/promotion/year-record`, {
        params: { id_student: row.id_student, academic_year: row.academic_year },
      });
      setYearRecord(res.data?.data || null);
    } catch (error) {
      Swal.fire("Could not open this year", error.response?.data?.msg || "Try again.", "error");
    } finally {
      setLoadingRecord(false);
    }
  };

  if (!allowed) {
    return (
      <div className="page-wrapper page-settings">
        <div className="content">
          <h5>Carry Forward</h5>
          <p>Only Founder and Admin can move students to the next standard.</p>
        </div>
      </div>
    );
  }

  const classSelects = (which, value, lists) => (
    <>
      <div className="col-lg-4">
        <div className="form-group">
          <label>Branch</label>
          <select className="form-select" value={value.id_branch} onChange={(e) => loadChain(which, "id_branch", e.target.value, value)}>
            <option value="">Select branch</option>
            {branches.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="col-lg-4">
        <div className="form-group">
          <label>Board</label>
          <select className="form-select" value={value.id_board} onChange={(e) => loadChain(which, "id_board", e.target.value, value)}>
            <option value="">Select board</option>
            {lists.boards.map((item) => (
              <option key={item.id} value={item.id}>{item.board}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="col-lg-4">
        <div className="form-group">
          <label>Medium</label>
          <select className="form-select" value={value.id_medium} onChange={(e) => loadChain(which, "id_medium", e.target.value, value)}>
            <option value="">Select medium</option>
            {lists.mediums.map((item) => (
              <option key={item.id} value={item.id}>{item.medium}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="col-lg-4">
        <div className="form-group">
          <label>Standard</label>
          <select className="form-select" value={value.id_standard} onChange={(e) => loadChain(which, "id_standard", e.target.value, value)}>
            <option value="">Select standard</option>
            {lists.standards.map((item) => (
              <option key={item.id} value={item.id}>{item.standard}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="col-lg-4">
        <div className="form-group">
          <label>Batch {which === "from" ? "(optional)" : ""}</label>
          <select className="form-select" value={value.id_batch} onChange={(e) => loadChain(which, "id_batch", e.target.value, value)}>
            <option value="">{which === "from" ? "All batches" : "Select batch"}</option>
            {lists.batches.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>
      </div>
    </>
  );

  return (
    <div className="page-wrapper page-settings">
      <div className="content">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><NavLink to="/">Dashboard </NavLink>/</li>
            <li className="breadcrumb-item active" aria-current="page">Carry Forward</li>
          </ol>
        </nav>
        <div className="content-page-header content-page-headersplit">
          <h5>Carry Forward</h5>
          <div className="list-btn">
            <ul>
              <li>
                <button type="button" className={`btn ${tab === "promote" ? "btn-primary" : "btn-outline-primary"} me-2`} onClick={() => setTab("promote")}>
                  Promote
                </button>
              </li>
              <li>
                <button type="button" className={`btn ${tab === "history" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setTab("history")}>
                  Past Years
                </button>
              </li>
            </ul>
          </div>
        </div>

        {tab === "promote" && (
          <>
            <p className="text-muted">
              Move students from the completed standard into the next one. Old attendance, homework, exams, marks and fees stay on the year they were recorded. Nothing from the past year is deleted.
            </p>
            <div className="card mb-3">
              <div className="card-body">
                <h6>Current class</h6>
                <div className="row">
                  {classSelects("from", fromClass, fromLists)}
                  <div className="col-lg-4">
                    <div className="form-group">
                      <label>Completed academic year</label>
                      <select className="form-select" value={fromYear} onChange={(e) => setFromYear(e.target.value)}>
                        {years.map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-lg-4 d-flex align-items-end">
                    <button type="button" className="btn btn-primary mb-3" onClick={showStudents} disabled={loadingStudents}>
                      {loadingStudents ? "Loading..." : "Show students"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <h6>Next class</h6>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={copyCurrentSetup}>
                    Same branch, board and medium
                  </button>
                </div>
                <div className="row">
                  {classSelects("to", toClass, toLists)}
                  <div className="col-lg-4">
                    <div className="form-group">
                      <label>Course</label>
                      <select className="form-select" value={toClass.id_course} onChange={(e) => setToClass({ ...toClass, id_course: e.target.value })}>
                        <option value="">No course</option>
                        {(toLists.courses || []).map((item) => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="form-group">
                      <label>Shift</label>
                      <select className="form-select" value={toClass.id_shift} onChange={(e) => setToClass({ ...toClass, id_shift: e.target.value })}>
                        <option value="">Keep current shift</option>
                        {shifts.map((item) => (
                          <option key={item.id} value={item.id}>{item.shift}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="form-group">
                      <label>Next academic year</label>
                      <select className="form-select" value={toYear} onChange={(e) => setToYear(e.target.value)}>
                        {years.map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-lg-8">
                    <div className="form-group">
                      <label>Remark</label>
                      <input className="form-control" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Optional note" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-3">
                  <h6 className="mb-0">Students ({visibleStudents.length})</h6>
                  <input className="form-control" style={{ maxWidth: 240 }} placeholder="Search name" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={visibleStudents.length > 0 && selected.length === visibleStudents.length}
                            onChange={(e) => toggleAll(e.target.checked)}
                          />
                        </th>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>Batch</th>
                        <th>Course</th>
                        <th>Shift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleStudents.map((student) => (
                        <tr key={student.id}>
                          <td>
                            <input type="checkbox" checked={selected.includes(student.id)} onChange={() => toggleOne(student.id)} />
                          </td>
                          <td>{student.name}</td>
                          <td>{student.contact_1}</td>
                          <td>{student.batch_name}</td>
                          <td>{student.course_name}</td>
                          <td>{student.shift_name}</td>
                        </tr>
                      ))}
                      {!visibleStudents.length && (
                        <tr>
                          <td colSpan="6">Select the current class and click Show students.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="btn btn-primary" onClick={carryForward} disabled={saving}>
                  {saving ? "Saving..." : `Carry forward selected (${selected.length})`}
                </button>
              </div>
            </div>
          </>
        )}

        {tab === "history" && (
          <div className="row">
            <div className="col-lg-7">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-5">
                      <input className="form-control mb-3" placeholder="Search student" value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} />
                    </div>
                    <div className="col-lg-4">
                      <select className="form-select mb-3" value={historyYear} onChange={(e) => setHistoryYear(e.target.value)}>
                        <option value="">All years</option>
                        {years.map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg-3">
                      <button type="button" className="btn btn-primary" onClick={loadHistory}>Search</button>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Year</th>
                          <th>Standard</th>
                          <th>Batch</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((row) => (
                          <tr key={row.id}>
                            <td>{row.student_name}</td>
                            <td>{row.academic_year}</td>
                            <td>{row.standard_name}</td>
                            <td>{row.batch_name}</td>
                            <td>{row.status}</td>
                            <td>
                              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openYear(row)}>
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!history.length && (
                          <tr>
                            <td colSpan="6">No carry-forward history yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="card">
                <div className="card-body">
                  <h6>Year record</h6>
                  {loadingRecord && <p>Loading...</p>}
                  {!yearRecord && !loadingRecord && <p className="text-muted">Open a year to see that student's old attendance, exams, homework and fees.</p>}
                  {yearRecord && (
                    <>
                      <p>
                        <strong>{yearRecord.session.student_name}</strong><br />
                        {yearRecord.session.academic_year} · {yearRecord.session.standard_name} · {yearRecord.session.batch_name}<br />
                        {yearRecord.session.branch_name} / {yearRecord.session.board_name} / {yearRecord.session.medium_name}
                      </p>
                      <p>Attendance: {yearRecord.attendance.length} &nbsp; Exams: {yearRecord.exams.length} &nbsp; Homework: {yearRecord.homework.length}</p>
                      <p>Homework status: {yearRecord.homework_status.length} &nbsp; Fees entries: {yearRecord.fees.length} &nbsp; PTM: {yearRecord.ptm.length}</p>
                      <h6 className="mt-3">Exams</h6>
                      <ul>
                        {yearRecord.exams.slice(0, 8).map((exam) => (
                          <li key={exam.id}>{exam.title} — {exam.marks ?? "-"} / {exam.total_marks}</li>
                        ))}
                        {!yearRecord.exams.length && <li>No exams for this standard.</li>}
                      </ul>
                      <h6>Fees</h6>
                      <ul>
                        {yearRecord.fees.slice(0, 8).map((fee) => (
                          <li key={fee.id}>{fee.created_on} — {fee.amount}</li>
                        ))}
                        {!yearRecord.fees.length && <li>No fees in April–March of this academic year.</li>}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CarryForward;
